import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface DateRangeOption {
  value: string;
  label: string;
}

interface DateRangeDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DateRangeOption[];
  placeholder?: string;
}

export const DateRangeDropdown: React.FC<DateRangeDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select range'
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = useMemo(
    () => options.find(opt => opt.value === value),
    [options, value]
  );

  // Close on outside click
  useEffect(() => {
    const handleOutside = (event: PointerEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, []);

  const handleSelect = useCallback(
    (option: DateRangeOption) => {
      onChange(option.value);
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <div className="flex flex-col gap-1 w-full sm:w-48">
      {/* Label */}
      <label className="text-xs sm:text-sm font-medium text-[var(--text-secondary)] px-1">
        Filter Type
      </label>
      
      <div ref={dropdownRef} className="relative w-full">
        {/* Trigger */}
        <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="
          group
          flex items-center justify-between
          w-full

          min-h-[44px]
          px-3 sm:px-4
          py-2.5

          rounded-xl
          border

          bg-[var(--bg-primary)]
          border-[var(--border-color)]

          text-sm sm:text-base
          text-[var(--text-primary)]

          transition-all duration-200

          hover:border-[#EF4421]
          focus:outline-none
          focus:ring-2 focus:ring-[#EF4421]/40

          active:scale-[0.98]

          transform-gpu
        "
      >
        <span className="truncate">
          {selectedOption?.label ?? placeholder}
        </span>

        <svg
          className={`
            w-4 h-4 sm:w-5 sm:h-5
            ml-2
            text-[var(--text-secondary)]
            transition-transform duration-200
            ${isOpen ? 'rotate-180' : 'rotate-0'}
          `}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown (NO layout shift flicker version) */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            role="listbox"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{
              duration: 0.16,
              ease: 'easeOut'
            }}
            style={{
              transformOrigin: 'top'
            }}
            className="
              absolute
              left-0 right-0
              mt-2
              z-[9999]

              rounded-xl
              border

              bg-[var(--bg-primary)]
              border-[var(--border-color)]

              shadow-xl

              max-h-64
              overflow-y-auto

              transform-gpu
              will-change-transform
            "
          >
            {options.map(option => {
              const active = option.value === value;

              return (
                <button
                  key={option.value}
                  role="option"
                  aria-selected={active}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className={`
                    w-full text-left
                    px-3 sm:px-4
                    py-3

                    text-sm sm:text-base

                    transition-colors duration-150

                    active:scale-[0.99]
                    transform-gpu

                    ${
                      active
                        ? 'bg-[#FEECE9] text-[#EF4421] font-medium'
                        : 'text-[var(--text-primary)] hover:bg-[#F8F8F8]'
                    }
                  `}
                >
                  <span className="truncate">
                    {option.label}
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};
